import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"
import _imports_0 from '@/assets/icons/arrow-left.svg'


const _hoisted_1 = { class: "mt-[-3.75remX]" }
const _hoisted_2 = { class: "max-w-[704px] mt-5X" }
const _hoisted_3 = { class: "flex w-[640px] items-start content-start gap-4 flex-wrap" }
const _hoisted_4 = { class: "sm:w-full md:w-auto" }
const _hoisted_5 = { class: "mt-10" }
const _hoisted_6 = { class: "mt-6" }
const _hoisted_7 = { class: "flex w-[640px] mt-14 items-start content-start gap-4 flex-wrap" }
const _hoisted_8 = { class: "flex items-center space-x-2" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "!mt-10 mb-10 md:!mt-12 md:!mb-12" }

import { onMounted, ref, nextTick } from 'vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { getFormInput } from '@/utils/form-input-helper';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import Divider from '@/components/Divider/Divider.vue';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import MembershipCardModal from '@/components/MembershipCard/MembershipCardModal.vue';
import { useSelfServicePhysicalMembershipCardStore, useAccountStore, useAnalyticsStore } from '@/store';
import { SelfServiceService } from '@/services/store';
import {
  CommunicationPreferenceOptions,
  CommunicationPreferences,
  getEnumValue,
} from '@/interfaces/self-service/communication-preferences';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { FormEvents } from '@/enums/form-events.enum';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { useI18n } from 'vue-i18n';
import { Link } from '@sitecore-jss/sitecore-jss-vue';
import { useRoute } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'MembershipCardPage',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const physicalCardStore = useSelfServicePhysicalMembershipCardStore();
const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const route = useRoute();

const props = __props;

const membershipCardModalModalEl = ref<InstanceType<typeof MembershipCardModal>>();
const showSavingOrLoading = ref<boolean>(false);
const hasChanges = ref<boolean>(false);

const model = ref<any>({ deliveryChannelMembershipCard: '' });
const subscriptions = <any>[];
const cardFormatMessage = ref(getFormInput(props.content, 'cardFormatMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const cardFormatOptions = [
  { label: 'Physical card', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.PostalPhysical] },
  { label: 'Digital card', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.DigitalEmail] },
];

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

onMounted(async () => {
  await loadPreference();

  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }

  if (route.query.showcard !== undefined) {
    onViewDigitalCardClick();
  }

  pushAnalytics();
});

const loadPreference = async () => {
  showSavingOrLoading.value = true;
  try {
    const d365Id = accountStore.identityDetails?.d365Id ?? '';
    if (d365Id) {
      subscriptions.values = await SelfServiceService.getSubscriptions(d365Id);

      model.value.deliveryChannelMembershipCard = getSelectedValueByValue(
        subscriptions.values.deliveryChannelMembershipCard
      );
    }
  } catch (error) {
    console.error(error);
  }

  await nextTick();
  showSavingOrLoading.value = false;
};

const onOptionChange = (e: any) => {
  if (showSavingOrLoading.value) return;
  hasChanges.value = true;
};

const getOptionsForInput = () => {
  return cardFormatOptions.map((opt) => {
    return opt.label;
  });
};

const onSave = async () => {
  try {
    const submitModel = {
      deliveryChannelMembershipCard: getSelectedValueByDisplayValue(model.value.deliveryChannelMembershipCard),
    } as CommunicationPreferences;

    if (!submitModel.deliveryChannelMembershipCard) {
      return;
    }

    showSavingOrLoading.value = true;

    const d365Id = accountStore.identityDetails?.d365Id ?? '';

    if (d365Id) {
      const response = await SelfServiceService.updateMembershipCardFormatPref(d365Id, submitModel);

      if (response) {
        hasChanges.value = false;
        showSavingOrLoading.value = false;
        toastAlertStore.addToastAlert({
          title: 'Membership card preferences updated',
          description: '',
          type: ToastAlertType.SUCCESS,
          duration: 6000,
        });

        await analyticsStore.pushAnalytics({
          event: FormEvents.COMPLETE,
          form: {
            name: 'Membership card',
            stepName: 'FormCompleted',
          },
          profileInfo: {
            memCardFormatPref: model.value.deliveryChannelMembershipCard,
          },
        });

        return;
      }
    }
  } catch (error) {
    showSavingOrLoading.value = false;
    toastAlertStore.addToastAlert({
      title: 'Unable to save changes',
      description: '',
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    await discardChanges();

    await analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Membership card',
        formErrorType: 'Unable to save changes',
        formErrorDetail: 'Unable to save changes',
      },
    });
  }
};

const discardChanges = async () => {
  await loadPreference();
};

const getSelectedValueByDisplayValue = (selectedOption: string) => {
  const selected = cardFormatOptions.find((x) => x.label.toLowerCase() === selectedOption?.toLowerCase());
  return selected?.value;
};

const getSelectedValueByValue = (value: string) => {
  const enumValue = getEnumValue(value);
  if (enumValue !== undefined) {
    const selected = cardFormatOptions.find((x) => x.value === CommunicationPreferenceOptions[enumValue]);
    return selected?.label;
  }
  return '';
};

const onViewDigitalCardClick = () => {
  if (!membershipCardModalModalEl?.value) return;
  membershipCardModalModalEl?.value.showModal();
};

const onOrderPhysicalCardClick = () => {
  setTimeout(() => {
    if (!membershipCardModalModalEl?.value) return;
    membershipCardModalModalEl?.value.showOrderPhysicalCardModal();
  }, 100);
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_FormKit, {
      id: "changePassword",
      ref: "formRef",
      name: "changePassword",
      type: "form",
      onSubmit: onSave
    }, {
      default: _withCtx(() => [
        _createVNode(FormStepInputs, {
          step: {
          stepName: _ctx.name,
          formInputs: _ctx.content,
        }
        }, {
          cardFormatMessage: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(InformationBox, {
                title: cardFormatMessage.value.title.value,
                class: "flex items-start gap-4 p-5 px-6 self-stretch",
                description: cardFormatMessage.value.content.value,
                type: cardFormatMessage.value.messageType.value,
                isCircle: true,
                useButtonSlot: true,
                hideIcon: false
              }, {
                buttons: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "link text-m",
                        "data-test-id": "Membership_Card_Modal_Order_Physical_Card",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (onOrderPhysicalCardClick()))
                      }, _toDisplayString(_unref(getFormInput)(props.content, 'orderPhysicalCardButtonText')?.label.value), 1)
                    ])
                  ])
                ]),
                _: 1
              }, 8, ["title", "description", "type"])
            ])
          ]),
          futureCardsTitle: _withCtx(() => [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(_unref(getFormInput)(props.content, 'futureCardsTitle'))), null, 16)
            ])
          ]),
          cardOptionsLabel: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_FormKit, {
                modelValue: model.value.deliveryChannelMembershipCard,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model.value.deliveryChannelMembershipCard) = $event)),
                label: _unref(getFormInput)(props.content, 'cardOptionsLabel')?.label?.value,
                innerClass: 'text-base',
                inputClass: "text-lg",
                type: "nativeRadio",
                name: "deliveryChannelNotices",
                outerClass: "-mt-1",
                options: getOptionsForInput(),
                onInput: _cache[2] || (_cache[2] = (e) => onOptionChange(e))
              }, null, 8, ["modelValue", "label", "options"])
            ])
          ]),
          _: 1
        }, 8, ["step"]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_unref(Link), {
            field: discardChangesLink.value?.link,
            class: "btn btn-tertiary btn-md"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_8, [
                _cache[3] || (_cache[3] = _createElementVNode("img", {
                  src: _imports_0,
                  alt: "left arrow",
                  class: "w-5 h-5 inline-block"
                }, null, -1)),
                _createElementVNode("span", null, _toDisplayString(discardChangesLink.value?.link?.value?.text), 1)
              ])
            ]),
            _: 1
          }, 8, ["field"]),
          _createElementVNode("button", {
            type: "button",
            disabled: !hasChanges.value,
            class: "btn btn-md btn-primary",
            onClick: onSave
          }, " Save changes ", 8, _hoisted_9)
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(Divider)
        ])
      ]),
      _: 1
    }, 512),
    (showSavingOrLoading.value)
      ? (_openBlock(), _createBlock(FullScreenSpinner, {
          key: 0,
          applyOpacity: true
        }))
      : _createCommentVNode("", true),
    _createVNode(MembershipCardModal, {
      ref_key: "membershipCardModalModalEl",
      ref: membershipCardModalModalEl,
      membershipCardModal: _unref(physicalCardStore).membershipCardModal
    }, null, 8, ["membershipCardModal"])
  ]))
}
}

})